import { AppRoutes } from '@/constants/routes';
import { Permissions } from '@/constants/permissions';
import type { Guide } from './types';

/**
 * The print run itself — selecting cheques, deciding what goes on the paper, and confirming.
 *
 * Deliberately separate from the "Printing cheques" guide in the Cheques section, which walks a
 * first-time reader through printing one cheque. This is the operator's reference for the three
 * decisions taken at the moment of printing — crossing, payee, rubber stamp — each of which
 * changes a financial instrument, and each of which is separately permissioned.
 */

export const chequePrintingGuides: Guide[] = [
  // ── 1. The print run ───────────────────────────────────────────────────────
  {
    slug: 'cheque-print-run',
    title: 'Choosing what goes on the paper',
    section: 'cheque-printing',
    level: 'Basics',
    summary:
      'Select cheques from the pending grid, then decide the crossing, the payee and the rubber '
      + 'stamp before a single sheet is spent.',
    menuPath: 'Banking & Cash → Cheque Print Queue',
    screens: [
      { label: 'Cheque Print Queue', route: AppRoutes.BANK_CHEQUE_PRINT_QUEUE, permission: Permissions.BNK_CHEQUE_PRINT },
      { label: 'Bank Accounts',      route: AppRoutes.BANK_ACCOUNTS,           permission: Permissions.BNK_BANK_ACCOUNT_VIEW },
    ],
    blocks: [
      { kind: 'para', text:
        'The pending grid lists cheques your vouchers have issued and not yet confirmed as printed. '
        + 'Tick the ones you are about to run, press Print Selected, and a dialog shows exactly what '
        + 'will appear on the paper before anything is spent.' },

      { kind: 'callout', tone: 'note', title: 'What is not offered',
        text:
          'A cancelled cheque, and one already presented, cleared or returned, has no checkbox at '
          + 'all. The paper has left the building, and printing the number again would put a second '
          + 'physical cheque into circulation for a number the bank has already seen. A cheque you '
          + 'have already confirmed is not offered either — printing that one again is a reprint, '
          + 'which is a different action with a different permission.' },

      { kind: 'heading', text: 'The three decisions' },
      { kind: 'table',
        headers: ['Decision', 'What it changes', 'Who may make it'],
        rows: [
          ['Crossing',
           'Who can bank the cheque. “A/C Payee Only” restricts it to the payee’s own account; an open cheque can be banked by whoever presents it.',
           'Needs the crossing-override permission'],
          ['Payee',
           'Who the cheque is made out to. Setting it to CASH turns a traceable supplier payment into a bearer instrument.',
           'Needs the CASH-payee permission, granted separately'],
          ['Rubber stamp',
           'Whether the layout’s authority wording prints. Nothing prints either way unless the layout has stamp wording and a position configured.',
           'Anyone who may print'],
        ] },

      { kind: 'para', text:
        'The two override permissions are separate on purpose. Opening a crossing and writing a '
        + 'cheque to CASH are not the same act, and a company that lets a clerk do the first should '
        + 'not have to let them do the second.' },

      { kind: 'heading', text: 'Crossing' },
      { kind: 'steps', steps: [
        { title: 'Review what each cheque already carries',
          detail:
            'The dialog lists every cheque in the run with its registered crossing. “Not stated” '
            + 'means no crossing was recorded and none will print.' },
        { title: 'Leave it alone, or change it for this run',
          detail:
            'Choosing a crossing applies it to every cheque in the run, for this run only. The '
            + 'cheques stay registered as they are — the print log records what the paper carried.' },
        { title: 'Understand what “Open” means',
          detail:
            'Open is a decision to print an uncrossed cheque, and is recorded as one. It is not the '
            + 'same as “not stated”, which is nobody having said.' },
      ] },

      { kind: 'callout', tone: 'warn', title: 'An open cheque is a bearer instrument',
        text:
          'It can be banked by whoever presents it. The dialog warns before you print, and every '
          + 'cheque in the run carries the change in its print log.' },

      { kind: 'heading', text: 'Where the default crossing comes from' },
      { kind: 'para', text:
        'Cheque stock is per bank account — a current account’s cheques are usually pre-printed '
        + 'crossed, a petty account’s may not be. Set Default Cheque Crossing on the bank account '
        + 'and every cheque a voucher issues from it is registered with that crossing. Leave it '
        + 'unset and cheques carry no crossing, which is how the system behaved before this setting '
        + 'existed.' },

      { kind: 'callout', tone: 'tip', title: 'Set it once, on the account',
        text:
          'Changing the crossing on every print run is a sign the account’s default is wrong. The '
          + 'run-level control is for the exception, not the rule.' },
    ],
    related: ['cheque-print-confirm', 'ap-payment-payee', 'cheque-printing'],
  },

  // ── 2. Confirming ──────────────────────────────────────────────────────────
  {
    slug: 'cheque-print-confirm',
    title: 'Confirming a print, and reprinting one',
    section: 'cheque-printing',
    level: 'Basics',
    summary:
      'Why a cheque stays in the queue after it prints, what confirming actually records, and how '
      + 'a jammed or spoiled sheet is handled.',
    menuPath: 'Banking & Cash → Cheque Print Queue',
    screens: [
      { label: 'Cheque Print Queue', route: AppRoutes.BANK_CHEQUE_PRINT_QUEUE, permission: Permissions.BNK_CHEQUE_PRINT },
    ],
    blocks: [
      { kind: 'callout', tone: 'tip', title: 'Printing is not confirming',
        text:
          'Sending a cheque to the printer changes nothing in the system. The cheque stays in the '
          + 'pending grid until you tell it the paper came out right.' },

      { kind: 'para', text:
        'That is deliberate. A print can be previewed and cancelled, the printer can jam, the '
        + 'paper can feed crooked, the toner can run out halfway down the page. None of those is a '
        + 'printed cheque, and a system that dequeued on “sent to printer” would leave you with a '
        + 'cheque the register believes exists and you are holding in pieces.' },

      { kind: 'heading', text: 'The sequence' },
      { kind: 'steps', steps: [
        { title: 'Print the run',
          detail: 'The cheques stay in the pending grid. Print them again as many times as the '
                + 'stationery takes — nothing is recorded yet.' },
        { title: 'Check the paper',
          detail: 'Alignment, the payee line, the crossing, the amount in words.' },
        { title: 'Confirm the print',
          where: 'Confirm print, on each row',
          detail:
            'This is the moment the system records that a physical cheque exists. It writes who '
            + 'confirmed it, and the date and time.' },
        { title: 'The cheque leaves the grid',
          detail: 'Only confirmed cheques are removed. Anything still listed has not been '
                + 'accounted for.' },
      ] },

      { kind: 'heading', text: 'Reprinting a confirmed cheque' },
      { kind: 'para', text:
        'Once a cheque is confirmed, printing it again is a reprint: it means a second piece of '
        + 'paper could carry that cheque number. It needs its own permission, and it needs a '
        + 'reason, which is stored with it.' },

      { kind: 'callout', tone: 'warn', title: 'A batch cannot reprint',
        text:
          'Reprints are done one at a time, because a batch has nowhere to put a reason and a '
          + 'reprint with no reason is the one thing the print log exists to prevent. If several '
          + 'cheques in a run were spoiled, reprint each of them individually.' },

      { kind: 'heading', text: 'What the log holds' },
      { kind: 'list', items: [
        'Every print and reprint of the cheque, in order, with its sequence number.',
        'Who confirmed each one, and the date and time.',
        'For a reprint, the reason given.',
        'The crossing and the payee actually printed, when either was changed for that run.',
        'The batch the print belonged to, so a run reads as one event rather than N.',
      ] },
      { kind: 'para', text:
        'This is what an auditor opens when a cheque number turns up twice. It answers how many '
        + 'times the number was printed, by whom, and why — which the cheque record alone cannot, '
        + 'because it describes the instrument rather than the pieces of paper.' },
    ],
    related: ['cheque-print-run', 'cheque-reports'],
  },
];
